import { ChangeEvent, HTMLAttributes, forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

interface TextAreaProps extends HTMLAttributes<HTMLTextAreaElement> {
  value: string;
  placeholder: string;
  maxLength?: number;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    { value, placeholder, maxLength, rows = 3, onChange, ...props },
    forwardedRef,
  ) => {
    return (
      <Container>
        <TextArea
          ref={forwardedRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          spellCheck={false}
          rows={rows}
          {...props}
        />
        {maxLength && (
          <MaxLength>
            {value.length} / {maxLength}
          </MaxLength>
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

const TextArea = styled.textarea`
  ${({ theme }) => css`
    box-sizing: border-box;
    width: 100%;
    padding: 11px 12px;
    border: 1px solid transparent;
    ${theme.fontStyle.text.body16_medium};
    border-radius: 8px;
    outline: none;
    background: ${theme.backgroundColor.onelevated};
    color: ${theme.backgroundColor.bg_light_1};
    resize: none;
    ${theme.mixins.scroll.default}

    &::placeholder {
      color: ${theme.content.content_light_5};
      ${theme.fontStyle.text.body16_medium};
    }

    &:focus {
      border: 1px solid ${theme.backgroundColor.onelevated_border};
    }
  `}
`;

const MaxLength = styled.span`
  ${({ theme }) => css`
    display: block;
    color: ${theme.content.content_light_4};
    ${theme.fontStyle.label.list12_medium};
    text-align: end;
  `}
`;

export default Textarea;
